/**
 * 노후화 분석 — 환경공단 원천 방안 2·4·5·6 (DESIGN §8.11.4).
 *
 * **환경부(ME) 직접설치분만이다.** 원천이 다르므로 공개 API 숫자와 나란히 놓되 섞지 않는다.
 * 정의는 기존 환경공단 대시보드(pipeline.py)에서 그대로 옮겼다 — 지속고장 = 설비 기인 ∧
 * (30분 이상 ∨ 미복구). 좌측 필터와 무관하다.
 */

import type { EChartsOption } from 'echarts'
import { useMemo, useState } from 'react'

import { ChartFrame, type LegendItem } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { axisStyle, CHART_H, rampAt, seqRamp } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import type { AgingKecoData, Envelope, KecoAgeCurveRow } from '@/lib/types'
import { useAgingKeco } from './queries'

/** 이 미만의 기·월 칸은 값이 아니라 표본이 아니다 — 빈 원으로 그린다 */
const THIN_CHARGER_MONTHS = 1000

function unavailable(env: Envelope<AgingKecoData> | undefined): string {
  return env && !env.data.available ? env.data.reason : ''
}

/** 방안 4 — 욕조곡선. 2025-03 수집정책 단절 전·후를 따로 그린다. */
export function Bathtub() {
  const q = useAgingKeco()
  const env = q.data as Envelope<AgingKecoData> | undefined
  const d = env?.data.available ? env.data : null
  const curve = useMemo(() => d?.age_curve ?? [], [d])
  const brk = d?.meta.policy_break_ym ?? '2025-03'

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const c2 = cssVar('--c-cat-2', '#a35200')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const ages = Array.from({ length: 10 }, (_, i) => i)
    const pick = (period: 'pre' | 'post', color: string, dashed: boolean, name: string) => {
      const by = new Map(curve.filter((r) => r.period === period).map((r) => [r.age, r]))
      return {
        name,
        type: 'line' as const,
        connectNulls: false,
        lineStyle: { width: 2, color, type: dashed ? ('dashed' as const) : ('solid' as const) },
        itemStyle: { color },
        symbolSize: 9,
        data: ages.map((a) => {
          const r = by.get(a)
          if (!r) return null
          const thin = r.charger_months < THIN_CHARGER_MONTHS
          return {
            value: r.fail_per_chg_month,
            // 얇은 칸은 빈 원 — 값은 있지만 표본이 아니다
            itemStyle: thin ? { color: cssVar('--c-canvas', '#fff'), borderColor: color, borderWidth: 2 } : { color },
          }
        }),
      }
    }
    return {
      grid: { left: 52, right: 16, top: 24, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => (v == null ? '—' : Number(v).toFixed(3)),
      },
      xAxis: {
        type: 'category',
        data: ages.map(String),
        name: '경과년수 (관측 연도 − 연식)',
        nameLocation: 'middle',
        nameGap: 26,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
      },
      yAxis: { type: 'value', ...axisStyle(ink3, hairline) },
      series: [
        pick('post', c1, false, `${brk} 이후`),
        pick('pre', c2, true, `${brk} 이전`),
      ],
    }
  }, [curve, brk])

  const post = curve.filter((r) => r.period === 'post' && r.charger_months >= THIN_CHARGER_MONTHS)
  const floor = post.reduce<KecoAgeCurveRow | null>(
    (a, r) => (a == null || (r.fail_per_chg_month ?? 0) < (a.fail_per_chg_month ?? 0) ? r : a),
    null,
  )
  const oldest = post.reduce<KecoAgeCurveRow | null>((a, r) => (a == null || r.age > a.age ? r : a), null)

  return (
    <ChartFrame
      title="욕조곡선 — 경과년수별 지속고장"
      question="설치 초기와 노후기에 고장이 몰리는가"
      meta={env?.meta}
      hideCoverage
      headline={
        floor && oldest && floor.fail_per_chg_month && oldest.fail_per_chg_month ? (
          <Headline
            value={`${(oldest.fail_per_chg_month / floor.fail_per_chg_month).toFixed(1)}배`}
            unit={`${oldest.age}년 ÷ 바닥(${floor.age}년)`}
            tone="warn"
          />
        ) : undefined
      }
      legend={[
        { label: `${brk} 이후`, color: cssVar('--c-cat-1', '#0066cc'), pattern: 'dot' },
        { label: `${brk} 이전`, color: cssVar('--c-cat-2', '#a35200'), pattern: 'line' },
        { label: `얇은 칸 (< ${int(THIN_CHARGER_MONTHS)} 기·월)`, color: cssVar('--c-ink-3', '#626266'), pattern: 'ring' },
      ]}
      encoding={[
        { role: '가로축', what: '경과년수 = 관측 연도 − 연식(STAT_ID 3~4번째 자리)' },
        { role: '세로축', what: '설비 기인 지속고장 / 충전기·월' },
      ]}
      reading="충전기 한 대가 한 달에 겪는 설비 기인 지속고장 건수입니다. 분모는 그 달 충전 세션이 있었던 충전기 수의 합(기·월)입니다."
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && curve.length === 0}
      emptyText={unavailable(env) || '환경공단 집계에 곡선이 없습니다.'}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: `${brk} 전·후를 비교하지 말 것.`,
          text: `알람 수집 정책이 ${brk} 에 바뀌어 알람 총량이 약 1/6 로 줄었습니다(노후지표 해석가이드 §7). 두 곡선은 각자의 모양만 읽습니다.`,
        },
        {
          label: '경과년수 한 칸에 연식은 1~2개뿐이다.',
          text: '관측이 2024년부터라 9년차는 2017년산 하나뿐입니다. 경과년수 효과와 세대(연식) 효과를 가를 수 없습니다 — 노후기의 상승이 늙어서인지 그 세대 기종이어서인지 이 곡선만으로는 모릅니다.',
        },
        {
          label: '최근 월은 과소.',
          text: '복구되지 않은 고장은 지속고장으로 세지만, 마지막 몇 주는 아직 30분이 안 지난 것이 섞여 있을 수 있습니다(우측 절단).',
        },
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="경과년수별 지속고장 표">
          <table className="data-table">
            <caption className="sr-only">경과년수별 지속고장</caption>
            <thead>
              <tr>
                <th scope="col">구간</th>
                <th scope="col">경과년수</th>
                <th scope="col">지속고장</th>
                <th scope="col">기·월</th>
                <th scope="col">기·월당</th>
                <th scope="col">연식 수</th>
                <th scope="col">관측 월</th>
              </tr>
            </thead>
            <tbody>
              {curve.map((r) => (
                <tr key={`${r.period}${r.age}`} className={r.charger_months < THIN_CHARGER_MONTHS ? 'is-muted' : ''}>
                  <th scope="row">{r.period === 'post' ? `${brk} 이후` : `${brk} 이전`}</th>
                  <td className="num">{r.age}</td>
                  <td className="num">{int(r.chg_fail)}</td>
                  <td className="num">{int(r.charger_months)}</td>
                  <td className="num">{r.fail_per_chg_month?.toFixed(3) ?? '—'}</td>
                  <td className="num">{r.n_cohorts}</td>
                  <td>
                    {r.ym_from} ~ {r.ym_to}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    >
      <EChart option={option} height={CHART_H} ariaLabel="경과년수별 지속고장 꺾은선" />
    </ChartFrame>
  )
}

/** 방안 2(환경공단) — 철거일 `DEL_DT` 로 그린 연식별 생존곡선 */
export function Survival() {
  const q = useAgingKeco()
  const env = q.data as Envelope<AgingKecoData> | undefined
  const d = env?.data.available ? env.data : null
  const surv = useMemo(() => [...(d?.survival ?? [])].sort((a, b) => a.cohort - b.cohort), [d])
  // 오래된 연식일수록 짙게 — 순서형이므로 범주색을 늘리지 않는다
  const colors = useMemo(() => seqRamp(surv.length).reverse(), [surv.length])

  const option = useMemo<EChartsOption>(() => {
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const maxAge = Math.max(0, ...surv.flatMap((s) => s.curve.map((c) => c.age)))
    const mins = surv.flatMap((s) => s.curve.map((c) => c.survival))
    const lo = Math.max(0, Math.floor((Math.min(1, ...mins) - 0.02) * 20) / 20)
    return {
      grid: { left: 52, right: 56, top: 20, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => (v == null ? '—' : pct(Number(v), 1)),
      },
      xAxis: {
        type: 'category',
        data: Array.from({ length: maxAge + 1 }, (_, i) => String(i)),
        name: '경과년수',
        nameLocation: 'middle',
        nameGap: 26,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
      },
      yAxis: {
        type: 'value',
        min: lo,
        max: 1,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: (v: number) => `${Math.round(v * 100)}%` },
      },
      series: surv.map((s, i) => ({
        name: `${s.cohort}년산`,
        type: 'line' as const,
        step: 'end' as const,
        symbol: 'none',
        lineStyle: { width: 2, color: rampAt(colors, i) },
        itemStyle: { color: rampAt(colors, i) },
        data: s.curve.map((c) => c.survival),
        endLabel: { show: true, fontSize: 10, color: ink3, formatter: `${s.cohort}` },
      })),
    }
  }, [surv, colors])

  const legend: LegendItem[] = surv.map((s, i) => ({ label: `${s.cohort}`, color: rampAt(colors, i), pattern: 'line' }))
  const nDel = surv.reduce((a, s) => a + s.deleted, 0)

  return (
    <ChartFrame
      title="연식별 생존곡선 — 철거일 기준"
      question="연식마다 몇 년째에 얼마나 철거되는가"
      meta={env?.meta}
      hideCoverage
      headline={surv.length ? <Headline value={int(nDel)} unit="기 철거 기록" /> : undefined}
      legend={legend}
      encoding={[
        { role: '가로축', what: '경과년수 = 철거 연도 − 연식 (1년 단위)' },
        { role: '세로축', what: '아직 철거되지 않은 비율 (Kaplan-Meier)' },
        { role: '색 짙기', what: '연식 — 오래될수록 짙다' },
      ]}
      reading="충전기 정보의 철거일(DEL_DT)을 사건으로, 철거되지 않은 충전기는 기준 연도에서 중도절단으로 둔 생존곡선입니다."
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && surv.length === 0}
      emptyText={unavailable(env) || '생존곡선을 그릴 연식이 없습니다.'}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '철거 사유가 없다.',
          text: '노후 철거와 이전·고출력 교체가 같은 사건으로 셉니다(요청서 F 계층이 필요한 이유). 곡선을 수명 분포로 읽으면 과대추정입니다.',
        },
        {
          label: '목록에 남은 철거분 기준.',
          text: '충전기 정보 파일(수집 시점 1회)에 남아 있는 철거분만 셉니다. 목록에서 아예 지워진 오래된 철거가 있다면 빠집니다.',
        },
        {
          label: '설치일이 아니라 연식.',
          text: '연식은 충전소 ID 의 연도 자리입니다. 같은 충전소에 나중에 추가된 충전기는 실제보다 오래된 것으로 잡힙니다. 마지막 해는 부분 연도입니다.',
        },
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="연식별 생존 표">
          <table className="data-table">
            <caption className="sr-only">연식별 생존</caption>
            <thead>
              <tr>
                <th scope="col">연식</th>
                <th scope="col">충전기</th>
                <th scope="col">철거</th>
                <th scope="col">마지막 경과년수</th>
                <th scope="col">생존</th>
              </tr>
            </thead>
            <tbody>
              {surv.map((s) => {
                const last = s.curve[s.curve.length - 1]
                return (
                  <tr key={s.cohort}>
                    <th scope="row">{s.cohort}</th>
                    <td className="num">{int(s.n)}</td>
                    <td className="num">{int(s.deleted)}</td>
                    <td className="num">{last?.age ?? '—'}</td>
                    <td className="num">{pct(last?.survival, 1)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      }
    >
      <EChart option={option} height={CHART_H} ariaLabel="연식별 생존곡선" />
    </ChartFrame>
  )
}

/** 방안 5 — 출력 달성률(평균 충전전력 ÷ 정격) 월별 추이, 연식별 */
export function PowerTrend() {
  const q = useAgingKeco()
  const env = q.data as Envelope<AgingKecoData> | undefined
  const d = env?.data.available ? env.data : null
  const power = useMemo(() => d?.power ?? [], [d])
  const ratings = useMemo(
    () => [...new Set(power.map((r) => r.rated_kw))].sort((a, b) => a - b),
    [power],
  )
  const [pick, setPick] = useState<number | null>(null)
  const rated = pick ?? (ratings.includes(100) ? 100 : (ratings[0] ?? null))
  const rows = useMemo(() => power.filter((r) => r.rated_kw === rated), [power, rated])
  const cohorts = useMemo(() => [...new Set(rows.map((r) => r.cohort))].sort((a, b) => a - b), [rows])
  const months = useMemo(() => [...new Set(rows.map((r) => r.ym))].sort(), [rows])
  const colors = useMemo(() => seqRamp(cohorts.length).reverse(), [cohorts.length])

  const option = useMemo<EChartsOption>(() => {
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    return {
      grid: { left: 52, right: 48, top: 20, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => (v == null ? '—' : pct(Number(v), 0)),
      },
      xAxis: {
        type: 'category',
        data: months,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3 },
      },
      yAxis: {
        type: 'value',
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: (v: number) => `${Math.round(v * 100)}%` },
      },
      series: cohorts.map((c, i) => {
        const by = new Map(rows.filter((r) => r.cohort === c).map((r) => [r.ym, r.achievement]))
        return {
          name: `${c}년산`,
          type: 'line' as const,
          symbol: 'none',
          connectNulls: false,
          lineStyle: { width: 2, color: rampAt(colors, i) },
          itemStyle: { color: rampAt(colors, i) },
          data: months.map((m) => by.get(m) ?? null),
          endLabel: { show: true, fontSize: 10, color: ink3, formatter: `${c}` },
        }
      }),
    }
  }, [rows, cohorts, months, colors])

  return (
    <ChartFrame
      title="출력 달성률 추이 — 평균 충전전력 ÷ 정격"
      question="같은 정격에서 오래된 연식의 출력이 떨어지는가, 그리고 해가 갈수록 떨어지는가"
      meta={env?.meta}
      hideCoverage
      legend={cohorts.map((c, i) => ({ label: String(c), color: rampAt(colors, i), pattern: 'line' as const }))}
      controls={
        ratings.length > 0 ? (
          <div className="det__seg" role="group" aria-label="정격 전력">
            {ratings.map((r) => (
              <button
                key={r}
                type="button"
                className={`det__segbtn${rated === r ? ' is-on' : ''}`}
                aria-pressed={rated === r}
                onClick={() => setPick(r)}
              >
                {r}kW
              </button>
            ))}
          </div>
        ) : undefined
      }
      encoding={[
        { role: '가로축', what: '월' },
        { role: '세로축', what: '세션 평균 전력의 월 중앙값 ÷ 정격' },
        { role: '색 짙기', what: '연식 — 오래될수록 짙다' },
      ]}
      reading="세션마다 충전량 ÷ 충전 시간으로 평균 전력을 구하고, 월·연식별 중앙값을 정격으로 나눴습니다."
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && rows.length === 0}
      emptyText={unavailable(env) || '이 정격에 그릴 칸이 없습니다.'}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '최대 출력이 아니다.',
          text: '평균 전력에는 충전 후반 감속(SOC 상승)이 들어 있어 정격보다 한참 낮은 것이 정상입니다. 요청서의 출력 달성률은 세션 최대출력 기준이고, 이것은 그 대리값입니다.',
        },
        {
          label: '차량이 정한다.',
          text: '평균 전력은 충전기보다 차량(수용 전력·SOC)에 크게 좌우됩니다. 연식 간 차이는 그 충전소에 오는 차량 구성 차이일 수 있습니다.',
        },
        {
          label: `월·연식 칸에 세션이 ${d?.power_min_sessions ?? 30}건 미만이면 그리지 않는다.`,
          text: '5분 미만 세션과 평균 전력이 정격의 1.2배를 넘는 세션(시각 오류)은 뺐습니다.',
        },
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="출력 달성률 (최근 월) 표">
          <table className="data-table">
            <caption className="sr-only">출력 달성률 (최근 월)</caption>
            <thead>
              <tr>
                <th scope="col">연식</th>
                <th scope="col">최근 월</th>
                <th scope="col">세션</th>
                <th scope="col">평균 전력 중앙값</th>
                <th scope="col">상위 10%</th>
                <th scope="col">달성률</th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map((c) => {
                const last = rows.filter((r) => r.cohort === c).sort((a, b) => a.ym.localeCompare(b.ym)).at(-1)
                return (
                  <tr key={c}>
                    <th scope="row">{c}</th>
                    <td>{last?.ym ?? '—'}</td>
                    <td className="num">{int(last?.kw_n)}</td>
                    <td className="num">{last?.kw_p50 != null ? `${last.kw_p50.toFixed(1)}kW` : '—'}</td>
                    <td className="num">{last?.kw_p90 != null ? `${last.kw_p90.toFixed(1)}kW` : '—'}</td>
                    <td className="num">{pct(last?.achievement, 0)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      }
    >
      <EChart option={option} height={CHART_H} ariaLabel="연식별 출력 달성률 추이" />
    </ChartFrame>
  )
}

/** 방안 6 — 누적 스트레스(월평균 충전량) 10분위별 지속고장률 */
export function Stress() {
  const q = useAgingKeco()
  const env = q.data as Envelope<AgingKecoData> | undefined
  const d = env?.data.available ? env.data : null
  const bins = useMemo(() => d?.stress ?? [], [d])

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    return {
      grid: { left: 52, right: 16, top: 20, bottom: 52 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const arr = Array.isArray(params) ? params : [params]
          const b = bins[(arr[0] as { dataIndex: number }).dataIndex]
          if (!b) return ''
          return `<b>${b.bin + 1}분위</b> 월 ${b.mwh_pm_lo.toFixed(1)}~${b.mwh_pm_hi.toFixed(1)}MWh<br/>지속고장 ${(
            b.fail_per_chg_month ?? 0
          ).toFixed(3)} / 기·월<br/>평균 경과년수 ${b.age_mean.toFixed(1)} · 200kW 이상 ${pct(b.share_200kw, 0)}`
        },
      },
      xAxis: {
        type: 'category',
        data: bins.map((b) => `${b.bin + 1}`),
        name: '월평균 충전량 10분위 (1 = 가장 적게 쓰임)',
        nameLocation: 'middle',
        nameGap: 28,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
      },
      yAxis: { type: 'value', ...axisStyle(ink3, hairline) },
      series: [
        {
          type: 'bar',
          barCategoryGap: '24%',
          itemStyle: { color: c1, borderRadius: [4, 4, 0, 0] },
          data: bins.map((b) => b.fail_per_chg_month),
        },
      ],
    }
  }, [bins])

  const top = bins.at(-1)
  const bottom = bins[0]

  return (
    <ChartFrame
      title="누적 스트레스 대비 지속고장"
      question="많이 쓰인 충전기가 더 고장나는가 — 연식과 별개로"
      meta={env?.meta}
      hideCoverage
      headline={
        top?.fail_per_chg_month && bottom?.fail_per_chg_month ? (
          <Headline
            value={`${(top.fail_per_chg_month / bottom.fail_per_chg_month).toFixed(1)}배`}
            unit="최다 ÷ 최소 분위"
          />
        ) : undefined
      }
      encoding={[
        { role: '가로축', what: '충전기의 월평균 충전량(MWh) 10분위' },
        { role: '세로축', what: '설비 기인 지속고장 / 충전기·월' },
      ]}
      reading={`이용 개월이 3개월 이상인 충전기를 월평균 충전량으로 10등분하고, 분위마다 지속고장을 이용 개월로 나눴습니다.`}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && bins.length === 0}
      emptyText={unavailable(env) || '분위를 만들 충전기가 부족합니다.'}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '이용과 고장은 서로를 움직인다.',
          text: '자주 고장나는 충전기는 덜 쓰여 낮은 분위로 갑니다(역인과). 낮은 분위의 높은 고장률은 스트레스가 아니라 그 결과일 수 있습니다.',
        },
        {
          label: '분위마다 구성이 다르다.',
          text: '많이 쓰이는 분위에는 고출력(200kW 이상)·최근 연식이 몰립니다. 표의 평균 경과년수와 200kW 비중을 함께 보세요.',
        },
        {
          label: '누적이 아니라 월평균이다.',
          text: '설치 이후 누적 kWh 가 없어(관측은 2024-01부터) 관측 기간의 월평균으로 대신합니다.',
        },
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="월평균 충전량 분위별 지속고장 표">
          <table className="data-table">
            <caption className="sr-only">월평균 충전량 분위별 지속고장</caption>
            <thead>
              <tr>
                <th scope="col">분위</th>
                <th scope="col">월 MWh</th>
                <th scope="col">충전기</th>
                <th scope="col">기·월</th>
                <th scope="col">지속고장</th>
                <th scope="col">기·월당</th>
                <th scope="col">평균 경과년수</th>
                <th scope="col">200kW 이상</th>
              </tr>
            </thead>
            <tbody>
              {bins.map((b) => (
                <tr key={b.bin}>
                  <th scope="row">{b.bin + 1}</th>
                  <td className="num">
                    {b.mwh_pm_lo.toFixed(1)}~{b.mwh_pm_hi.toFixed(1)}
                  </td>
                  <td className="num">{int(b.n)}</td>
                  <td className="num">{int(b.charger_months)}</td>
                  <td className="num">{int(b.chg_fail)}</td>
                  <td className="num">{b.fail_per_chg_month?.toFixed(3) ?? '—'}</td>
                  <td className="num">{b.age_mean.toFixed(1)}</td>
                  <td className="num">{pct(b.share_200kw, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    >
      <EChart option={option} height={CHART_H} ariaLabel="월평균 충전량 분위별 지속고장 막대" />
    </ChartFrame>
  )
}
